import { ICommand } from '../../../core/commands/ICommand';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { DeleteEdgeCommand } from '../../../core/commands/edges/DeleteEdgeCommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useFlowchartLayoutStore } from '../../../stores/flowchartLayoutStore';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import { MoveNodesCommand } from '../../genogram/tools/GenogramTransformCommands';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import { ProcessLayoutService } from '../services/ProcessLayoutService';
import { getConnectedFlowchartNodeIds } from '../services/processGraph.utils';
import {
  createNodeId,
  createEdgeId,
  type NodeAddition,
  type EdgeAddition,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';
import { getTranslatedBpmnShapeLabel, getTranslatedFlowchartShapeLabel } from '../shapes/flowchart/flowchartShapeI18n';
import { directionFromDelta, planQuickCreatePlacement } from '../../../core/layout/spatialIntent';

export interface InsertStepBetweenParams {
  edgeId: string;
  //variant?: 'process' | 'decision' | 'start-end' | 'data' | 'document' | 'bpmn-event' | 'bpmn-gateway' | 'bpmn-activity' | 'bpmn-data' | 'bpmn-data-store' | 'bpmn-annotation'; // Defaults to 'process'

  variant?: string; // Defaults to 'process'
  midpointX: number;
  midpointY: number;
  /** Optional custom label for the inserted step (defaults to the variant's name). */
  label?: string;
  /** Optional swimlane name to stamp on the inserted step. */
  lane?: string;
}

/**
 * Command to insert a flowchart step between two connected nodes.
 * 
 * This command atomically:
 * 1. Deletes the original edge
 * 2. Creates a new node at the midpoint (or slightly offset for visibility)
 * 3. Creates two new edges: `from → new`, `new → to`
 * 
 * The operation is atomic and fully undoable/redoable.
 */
export class InsertStepBetweenCommand implements ICommand, IScenarioRecordable {
  id: string;
  name: string;
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: InsertStepBetweenParams;
  private originalEdge?: any;
  private createdShapeId?: string;
  private createdEdge1Id?: string;
  private createdEdge2Id?: string;
  private deletedEdgeId?: string;
  private moveCommands: MoveNodesCommand[] = [];
  private edgeUpdateCommands: UpdateEdgeCommand[] = [];
  private scenarioNodeId: ReturnType<typeof createNodeId>;
  private scenarioEdgeId1: ReturnType<typeof createEdgeId>;
  private scenarioEdgeId2: ReturnType<typeof createEdgeId>;

  constructor(params: InsertStepBetweenParams) {
    this.id = `insert-step-between-${Date.now()}`;
    this.name = 'Insert Step Between';
    this.description = 'Insert flowchart step between connected nodes';
    this.timestamp = Date.now();
    this.params = params;
    this.scenarioNodeId = createNodeId(`scenario-${this.id}`);
    this.scenarioEdgeId1 = createEdgeId(`scenario-${this.id}-edge-1`);
    this.scenarioEdgeId2 = createEdgeId(`scenario-${this.id}-edge-2`);
  }

  async execute(): Promise<void> {
    const store = useDiagramStore.getState();
    const edge = store.edges.find(e => e.id === this.params.edgeId);
    if (!edge) {
      throw new Error(`Edge ${this.params.edgeId} not found`);
    }

    const fromNode = store.nodes.find(n => n.id === edge.from);
    const toNode = store.nodes.find(n => n.id === edge.to);

    if (!fromNode || !toNode) {
      throw new Error('Both source and target nodes must exist');
    }

    const oppositeSide = (s: 'top' | 'right' | 'bottom' | 'left'): 'top' | 'right' | 'bottom' | 'left' => {
      switch (s) {
        case 'top': return 'bottom';
        case 'right': return 'left';
        case 'bottom': return 'top';
        case 'left': return 'right';
      }
    };

    const originalFromAnchor = (((edge as any).fromAnchor as any) ?? 'right') as 'top' | 'right' | 'bottom' | 'left';
    const originalToAnchor = (((edge as any).toAnchor as any) ?? 'left') as 'top' | 'right' | 'bottom' | 'left';
    const originalFromOffset = (typeof (edge as any).fromOffset === 'number' ? (edge as any).fromOffset : 0.5) as number;
    const originalToOffset = (typeof (edge as any).toOffset === 'number' ? (edge as any).toOffset : 0.5) as number;

    // ShapeC should "face" shapeA and shapeB:
    // - side towards A is opposite of A's connected side (original fromAnchor)
    // - side towards B is opposite of B's connected side (original toAnchor)
    const cSideTowardsA = oppositeSide(originalFromAnchor);
    const cSideTowardsB = oppositeSide(originalToAnchor);

    // Store original edge for undo
    this.originalEdge = { ...edge };
    this.deletedEdgeId = edge.id;

    // Determine variant (default to 'process')
    const variant = this.params.variant || 'process';

    // Get default dimensions from manifest
    const manifest = getShapeManifest('flowchart', variant);
    const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };
    const newW = defaultDims.width;
    const newH = defaultDims.height;
    const fromWidth = (fromNode.w ?? fromNode.width ?? 120) as number;
    const fromHeight = (fromNode.h ?? fromNode.height ?? 80) as number;
    const toWidth = (toNode.w ?? toNode.width ?? 120) as number;
    const toHeight = (toNode.h ?? toNode.height ?? 80) as number;
    const fromCenter = { x: (fromNode.x ?? 0) + fromWidth / 2, y: (fromNode.y ?? 0) + fromHeight / 2 };
    const toCenter = { x: (toNode.x ?? 0) + toWidth / 2, y: (toNode.y ?? 0) + toHeight / 2 };
    const insertionDirection = directionFromDelta(toCenter.x - fromCenter.x, toCenter.y - fromCenter.y);
    const layoutStoreAtStart = useFlowchartLayoutStore.getState();
    const manualPlacement = layoutStoreAtStart.autoLayoutMode === 'off'
      ? planQuickCreatePlacement({
          sourceNodeId: edge.from,
          nodes: store.nodes,
          edges: store.edges,
          requestedDirection: insertionDirection,
          newNodeSize: { width: newW, height: newH },
          gap: 40,
        })
      : null;

    // Position new node at midpoint, slightly offset for visibility
    // Offset by half the height to avoid overlapping the connector line
    const offsetY = -newH / 2 - 10; // Offset upward by half height + 10px
    const newX = manualPlacement?.position.x ?? (this.params.midpointX - newW / 2);
    const newY = manualPlacement?.position.y ?? (this.params.midpointY + offsetY);

    // Generate IDs for new edges (shape id comes from CreateShapeCommand)
    const timestamp = Date.now();
    const random = Math.random().toString(36).slice(2, 9);
    const edge1Id = `edge-${timestamp + 1}-${random}`;
    const edge2Id = `edge-${timestamp + 2}-${random}`;

    const mutationManager = getNodeMutationManager();
    mutationManager.runInTransaction(() => {
      const existingEdge = mutationManager.getEdge(this.params.edgeId);
      if (existingEdge) {
        this.originalEdge = { ...existingEdge, attrs: { ...(existingEdge.attrs || {}) } };
      }

      mutationManager.deleteEdge(this.params.edgeId);

      const isBpmnVariant = variant.startsWith('bpmn-');
      const label = this.params.label ?? (isBpmnVariant
        ? getTranslatedBpmnShapeLabel(variant)
        : getTranslatedFlowchartShapeLabel(variant));
      const shapeCategory = isBpmnVariant ? 'bpmn' : 'flowchart';
      const shapeAttrs: Record<string, unknown> = { label };
      if (this.params.lane) shapeAttrs.lane = this.params.lane;
      const shapeCmd = new CreateShapeCommand({
        type: variant,
        category: shapeCategory,
        x: newX,
        y: newY,
        width: newW,
        height: newH,
        text: label,
        attrs: shapeAttrs,
      });
      shapeCmd.execute();
      this.createdShapeId = shapeCmd.getCreatedId();
      if (!this.createdShapeId) {
        throw new Error('Failed to create shape: no ID returned');
      }

      const baseAttrs = { ...(edge.attrs || {}) } as Record<string, unknown>;
      if ('fromPoint' in baseAttrs) delete (baseAttrs as any).fromPoint;
      if ('toPoint' in baseAttrs) delete (baseAttrs as any).toPoint;
      const routingType = (baseAttrs as any).routingType ?? 'orthogonalRounded';
      baseAttrs.routingType = routingType;

      const edge1Cmd = new CreateEdgeCommand({
        id: edge1Id,
        type: 'sequence_flow',
        from: edge.from,
        to: this.createdShapeId,
        category: 'flowchart',
        // connectorA:
        // - keep same attachment point on shapeA as original connector's from end
        // - attach to shapeC on the side that corresponds to where shapeB was connected
        fromAnchor: originalFromAnchor,
        fromOffset: originalFromOffset,
        // NOTE: sides were reversed in practice; for A → C → B the "to" end should
        // land on C's side facing A.
        toAnchor: cSideTowardsA,
        toOffset: 0.5,
        attrs: baseAttrs as any,
      });
      edge1Cmd.execute();
      this.createdEdge1Id = edge1Id;

      const edge2Cmd = new CreateEdgeCommand({
        id: edge2Id,
        type: 'sequence_flow',
        from: this.createdShapeId,
        to: edge.to,
        category: 'flowchart',
        // connectorB:
        // - attach to shapeC on the side that corresponds to where shapeA was connected
        // - keep same attachment point on shapeB as original connector's to end
        // For A → C → B the "from" end should leave C on the side facing B.
        fromAnchor: cSideTowardsB,
        fromOffset: 0.5,
        toAnchor: originalToAnchor,
        toOffset: originalToOffset,
        attrs: baseAttrs as any,
      });
      edge2Cmd.execute();
      this.createdEdge2Id = edge2Id;
    });

    const waitForSync = async () => {
      await new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(() => resolve(), 0);
        }
      });
    };
    await waitForSync();
    await waitForSync();

    const postStore = useDiagramStore.getState();
    const createdNode = postStore.nodes.find((n) => n.id === this.createdShapeId);
    if (manualPlacement?.moves.length) {
      const cmd = new MoveNodesCommand(manualPlacement.moves);
      await cmd.execute();
      this.moveCommands.push(cmd);
    } else if (createdNode) {
      const getDims = (n: any) => {
        const w = (n?.w ?? n?.width ?? 120) as number;
        const h = (n?.h ?? n?.height ?? 80) as number;
        return { w, h };
      };
      const fromDims = getDims(fromNode);
      const toDims = getDims(toNode);
      const newDims = getDims(createdNode);

      const fromCenter = { x: (fromNode.x ?? 0) + fromDims.w / 2, y: (fromNode.y ?? 0) + fromDims.h / 2 };
      const toCenter = { x: (toNode.x ?? 0) + toDims.w / 2, y: (toNode.y ?? 0) + toDims.h / 2 };
      const dx = toCenter.x - fromCenter.x;
      const dy = toCenter.y - fromCenter.y;

      const horizontal = Math.abs(dx) >= Math.abs(dy);
      const targetPos = horizontal
        ? {
            x: (fromCenter.x + toCenter.x) / 2 - newDims.w / 2,
            // Keep the inserted node centered on the original connector line.
            y: this.params.midpointY - newDims.h / 2,
          }
        : {
            // Keep the inserted node centered on the original connector line.
            x: this.params.midpointX - newDims.w / 2,
            y: (fromCenter.y + toCenter.y) / 2 - newDims.h / 2,
          };

      const epsilon = 0.01;
      const oldPos = { x: createdNode.x ?? 0, y: createdNode.y ?? 0 };
      if (Math.abs(targetPos.x - oldPos.x) >= epsilon || Math.abs(targetPos.y - oldPos.y) >= epsilon) {
        const cmd = new MoveNodesCommand([
          { nodeId: createdNode.id, oldPosition: oldPos, newPosition: targetPos },
        ]);
        await cmd.execute();
        this.moveCommands.push(cmd);
      }
    }

    // Check auto-layout mode from store and apply layout if enabled
    const layoutStore = useFlowchartLayoutStore.getState();
    if (layoutStore.autoLayoutMode !== 'off' && this.createdShapeId) {
      const store = useDiagramStore.getState();

      const layoutService = new ProcessLayoutService();
      const layoutOptions = {
        strategy: layoutStore.layoutStrategy,
        nodeSpacing: 80,
        rankSpacing: 100,
      };

      let layout;
      if (layoutStore.autoLayoutMode === 'full') {
        const connectedNodeIds = getConnectedFlowchartNodeIds(
          store.nodes,
          store.edges,
          [this.createdShapeId, (edge as any).from as string, (edge as any).to as string].filter(Boolean)
        );
        layout = await layoutService.incrementalLayout(store.nodes, store.edges, connectedNodeIds, layoutOptions);
      } else {
        const affectedNodeIds = [this.createdShapeId, edge.from, edge.to];
        layout = await layoutService.incrementalLayout(store.nodes, store.edges, affectedNodeIds, layoutOptions);
      }

      if (layout.nodePositions.size > 0) {
        // Prevent the entire flow from "teleporting" to a new area by anchoring
        // the layout output to an existing node's current position (edge.from).
        const anchorId = (edge as any).from as string;
        const anchorBefore = store.nodes.find((n) => n.id === anchorId) as any;
        const anchorLayout = anchorId ? layout.nodePositions.get(anchorId) : undefined;
        const dx = anchorBefore && anchorLayout ? (anchorBefore.x ?? 0) - anchorLayout.x : 0;
        const dy = anchorBefore && anchorLayout ? (anchorBefore.y ?? 0) - anchorLayout.y : 0;

        const moves = Array.from(layout.nodePositions.entries()).map(([nodeId, newPos]) => {
          const n = store.nodes.find(nn => nn.id === nodeId);
          return {
            nodeId,
            oldPosition: n ? { x: n.x ?? 0, y: n.y ?? 0 } : undefined,
            newPosition: { x: newPos.x + dx, y: newPos.y + dy },
          };
        });

        if (moves.length > 0) {
          const cmd = new MoveNodesCommand(moves);
          await cmd.execute();
          this.moveCommands.push(cmd);
        }
      }
    }
    // No additional anchor mutation required: the two new edges were created
    // with anchors/offsets derived from the original edge per the rules above.
  }

  async undo(): Promise<void> {
    if (!this.originalEdge || !this.createdShapeId || !this.createdEdge1Id || !this.createdEdge2Id) {
      return;
    }

    const mutationManager = getNodeMutationManager();

    for (let i = this.edgeUpdateCommands.length - 1; i >= 0; i -= 1) {
      try { this.edgeUpdateCommands[i]!.undo(); } catch {}
    }
    for (let i = this.moveCommands.length - 1; i >= 0; i -= 1) {
      await this.moveCommands[i]!.undo();
    }

    mutationManager.runInTransaction(() => {
      const deleteEdge2Cmd = new DeleteEdgeCommand(this.createdEdge2Id!);
      deleteEdge2Cmd.execute();
      
      const deleteEdge1Cmd = new DeleteEdgeCommand(this.createdEdge1Id!);
      deleteEdge1Cmd.execute();

      mutationManager.deleteNode(this.createdShapeId!);

      const restoreEdgeCmd = new CreateEdgeCommand({
        id: this.deletedEdgeId!,
        type: this.originalEdge.type,
        from: this.originalEdge.from,
        to: this.originalEdge.to,
        category: (this.originalEdge as any).category || 'flowchart',
        fromAnchor: (this.originalEdge as any).fromAnchor,
        toAnchor: (this.originalEdge as any).toAnchor,
        fromOffset: (this.originalEdge as any).fromOffset,
        toOffset: (this.originalEdge as any).toOffset,
        attrs: this.originalEdge.attrs,
      });
      restoreEdgeCmd.execute();
    });
  }

  redo(): void {
    this.execute();
  }

  /** The id of the step created by this insert, available after execute(). */
  getCreatedShapeId(): string | undefined {
    return this.createdShapeId;
  }

  canExecute(): boolean {
    const store = useDiagramStore.getState();
    const edge = store.edges.find(e => e.id === this.params.edgeId);
    return !!edge && !!store.nodes.find(n => n.id === edge.from) && !!store.nodes.find(n => n.id === edge.to);
  }

  canUndo(): boolean {
    return true;
  }

  private getVariant(): 'process' | 'decision' | 'terminator' | 'data' | 'document' {
    const variant = this.params.variant ?? 'process';
    // Map 'start-end' to 'terminator' for compatibility
    if (variant === 'start-end') return 'terminator';
    // Map AI id for data shape back to the scenario-friendly key
    if (variant === 'input-output') return 'data';

    if (
      variant === 'process' ||
      variant === 'decision' ||
      variant === 'terminator' ||
      variant === 'data' ||
      variant === 'document'
    ) {
      return variant;
    }

    // Scenario recording currently supports the core variants only.
    return 'process';
  }

  private getScenarioProps(variant: string): Record<string, unknown> {
    switch (variant) {
      case 'process':
        return { description: '', activityType: 'task' };
      case 'decision':
        return { description: '', gatewayType: 'exclusive' };
      case 'terminator':
        return { description: '', terminatorType: 'start' };
      default:
        return {};
    }
  }

  private buildScenarioAddition(): NodeAddition {
    const variant = this.getVariant();
    const manifest = getShapeManifest('flowchart', variant);
    const defaultDims = manifest?.defaultDimensions || { width: 120, height: 80 };
    const label = variant
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    return {
      id: this.scenarioNodeId,
      type: variant,
      name: label,
      position: {
        x: this.params.midpointX - defaultDims.width / 2,
        y: this.params.midpointY - defaultDims.height / 2,
      },
      props: this.getScenarioProps(variant),
    };
  }

  private buildScenarioEdges(): { additions: EdgeAddition[]; removals: { edgeId: ReturnType<typeof createEdgeId> }[] } {
    const store = useDiagramStore.getState();
    const edge = store.edges.find((e) => e.id === this.params.edgeId) as any;
    if (!edge) {
      return { additions: [], removals: [] };
    }

    const originalFromAnchor = (((edge as any).fromAnchor as any) ?? 'right') as
      | 'top'
      | 'right'
      | 'bottom'
      | 'left';
    const originalToAnchor = (((edge as any).toAnchor as any) ?? 'left') as
      | 'top'
      | 'right'
      | 'bottom'
      | 'left';
    const originalFromOffset = (typeof (edge as any).fromOffset === 'number'
      ? (edge as any).fromOffset
      : 0.5) as number;
    const originalToOffset = (typeof (edge as any).toOffset === 'number' ? (edge as any).toOffset : 0.5) as number;

    const oppositeSide = (s: 'top' | 'right' | 'bottom' | 'left'): 'top' | 'right' | 'bottom' | 'left' => {
      switch (s) {
        case 'top':
          return 'bottom';
        case 'right':
          return 'left';
        case 'bottom':
          return 'top';
        case 'left':
          return 'right';
      }
    };

    const cSideTowardsA = oppositeSide(originalFromAnchor);
    const cSideTowardsB = oppositeSide(originalToAnchor);
    const routingType = ((edge as any).attrs?.routingType as string | undefined) ?? 'orthogonalRounded';

    const edge1: EdgeAddition = {
      id: this.scenarioEdgeId1,
      type: 'sequence_flow',
      from: createNodeId(edge.from as string),
      to: this.scenarioNodeId,
      props: {
        fromAnchor: originalFromAnchor,
        fromOffset: originalFromOffset,
        toAnchor: cSideTowardsA,
        toOffset: 0.5,
        routingType,
      },
    };

    const edge2: EdgeAddition = {
      id: this.scenarioEdgeId2,
      type: 'sequence_flow',
      from: this.scenarioNodeId,
      to: createNodeId(edge.to as string),
      props: {
        fromAnchor: cSideTowardsB,
        fromOffset: 0.5,
        toAnchor: originalToAnchor,
        toOffset: originalToOffset,
        routingType,
      },
    };

    return {
      additions: [edge1, edge2],
      removals: [{ edgeId: createEdgeId(edge.id as string) }],
    };
  }

  private buildOriginalEdgeAddition(): EdgeAddition | null {
    const store = useDiagramStore.getState();
    const edge = store.edges.find((e) => e.id === this.params.edgeId) as any;
    if (!edge) return null;

    return {
      id: createEdgeId(edge.id as string),
      type: (edge.type as string) ?? 'sequence_flow',
      from: createNodeId(edge.from as string),
      to: createNodeId(edge.to as string),
      props: {
        ...(edge.attrs ?? {}),
        fromAnchor: (edge as any).fromAnchor,
        toAnchor: (edge as any).toAnchor,
        fromOffset: (edge as any).fromOffset,
        toOffset: (edge as any).toOffset,
      },
    };
  }

  toScenarioDelta(): CommandDeltaContribution {
    const edges = this.buildScenarioEdges();
    return {
      additions: [this.buildScenarioAddition()],
      edgeAdditions: edges.additions,
      edgeRemovals: edges.removals,
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const addition = this.buildScenarioAddition();
    const edges = this.buildScenarioEdges();
    const originalEdge = this.buildOriginalEdgeAddition();
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: addition.name,
            type: addition.type,
          },
        },
      ],
      edgeAdditions: originalEdge ? [originalEdge] : [],
      edgeRemovals: edges.additions.map((edge) => ({ edgeId: edge.id })),
    };
  }

}
